import { UpcomingMatch } from "../types/types";

const TWITCH_HOST = "twitch.tv";
const RESERVED_TWITCH_SEGMENTS = new Set([
  "videos",
  "directory",
  "settings",
  "subscriptions",
  "inventory",
  "wallet",
  "drops",
  "popout",
  "embed",
  "p",
  "clip",
  "clips",
]);

const TWO_HOURS_MS = 2 * 60 * 60 * 1000;
const ONE_HOUR_SECONDS = 3600;

export const parseTwitchChannelFromUrl = (rawUrl: string): string | null => {
  const trimmed = rawUrl.trim();
  if (!trimmed) {
    return null;
  }

  try {
    const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    const url = new URL(withProtocol);
    const host = url.hostname.replace(/^www\./i, "").toLowerCase();
    if (host !== TWITCH_HOST && !host.endsWith(`.${TWITCH_HOST}`)) {
      return null;
    }

    if (host === "player.twitch.tv") {
      const channel = url.searchParams.get("channel")?.trim().toLowerCase();
      return channel || null;
    }

    const segments = url.pathname.split("/").filter(Boolean);
    const firstSegment = segments[0]?.toLowerCase() ?? "";
    if (!firstSegment) {
      return null;
    }

    if (firstSegment === "embed" && segments[1]) {
      const embeddedChannel = decodeURIComponent(segments[1]).trim().toLowerCase();
      return embeddedChannel || null;
    }

    if (RESERVED_TWITCH_SEGMENTS.has(firstSegment)) {
      return null;
    }

    return firstSegment;
  } catch {
    return null;
  }
};

/** Match whose stream link should drive the home livestream embed. */
export const getActiveStreamMatch = (
  upcomingMatches: UpcomingMatch[] | undefined,
  remainingSeconds: number,
): UpcomingMatch | undefined => {
  if (!upcomingMatches?.length || remainingSeconds > ONE_HOUR_SECONDS) {
    return undefined;
  }

  const sorted = [...upcomingMatches].sort(
    (a, b) => new Date(a.time).getTime() - new Date(b.time).getTime(),
  );
  const now = Date.now();

  if (remainingSeconds === 0) {
    return (
      sorted.find((match) => {
        const matchTime = new Date(match.time).getTime();
        return matchTime <= now && matchTime >= now - TWO_HOURS_MS;
      }) ?? sorted[0]
    );
  }

  return sorted.find((match) => new Date(match.time).getTime() > now) ?? sorted[0];
};

export const getStreamChannelFromMatch = (match: UpcomingMatch | undefined): string | null => {
  if (!match?.link) {
    return null;
  }
  return parseTwitchChannelFromUrl(match.link);
};
